export type LiveVoiceTimingStage =
  | "started"
  | "audio_prepared"
  | "token_ready"
  | "context_ready"
  | "socket_open"
  | "setup_ready"
  | "microphone_ready"
  | "tool_started"
  | "tool_accepted"
  | "tool_result"
  | "first_pcm_scheduled"
  | "audio_repair"
  | "audio_recovered"
  | "ended";
export type LiveVoiceTimingEvent = {
  traceId: string;
  stage: LiveVoiceTimingStage;
  sequence: number;
  operation: number;
  elapsedMs: number;
  sincePreviousMs: number;
};
const MEASURE = "hrokbot.live.voice";
const MAX_EVENTS = 128;
const events: LiveVoiceTimingEvent[] = [];

/** Local diagnostics only: no transcript, URL, account, credential or financial data.
 * Лише локальна діагностика: без реплік, адрес, акаунтів, ключів і фінансових даних. */
export function liveVoiceTimingEvents(): LiveVoiceTimingEvent[] {
  return events.map((event) => ({ ...event }));
}
function record(event: LiveVoiceTimingEvent): void {
  events.push({ ...event });
  if (events.length > MAX_EVENTS) events.shift();
  if (typeof performance?.measure !== "function") return;
  if (performance.getEntriesByName(MEASURE).length >= MAX_EVENTS)
    performance.clearMeasures(MEASURE);
  performance.measure(MEASURE, { start: 0, duration: event.elapsedMs, detail: { ...event } });
}
export class LiveVoiceTiming {
  private readonly started: number;
  private sequence = 0;
  private lastElapsed = 0;
  private operation = 0;
  private previous = new Map<number, number>();
  constructor(
    private readonly sink: (event: LiveVoiceTimingEvent) => void = record,
    private readonly now: () => number = () => performance.now(),
    private readonly traceId: string = crypto.randomUUID(),
  ) {
    this.started = this.now();
    this.mark("started");
  }
  beginTask(): number {
    const operation = ++this.operation;
    this.mark("tool_started", operation);
    return operation;
  }
  mark(stage: LiveVoiceTimingStage, operation = 0): void {
    const raw = this.now() - this.started;
    if (!Number.isFinite(raw)) return;
    const prior = this.previous.get(operation) ?? 0;
    const elapsedMs = Math.max(this.lastElapsed, prior, 0, raw);
    this.lastElapsed = elapsedMs;
    this.previous.set(operation, elapsedMs);
    try {
      this.sink({
        traceId: this.traceId,
        stage,
        sequence: ++this.sequence,
        operation,
        elapsedMs: Math.round(elapsedMs),
        sincePreviousMs: Math.round(elapsedMs - prior),
      });
    } catch {
      // Diagnostics cannot interrupt voice delivery. / Діагностика не перериває голос.
    }
    if (stage === "tool_result") this.previous.delete(operation);
  }
}
